/**
 * Status (text) widget ranges — balancer status, modes, etc.
 * Mirrors backend/utils/dashboardStatusAlarmUtils.js
 */

const STATUS_DATA_PATH_RE = /balancer|operatingstate|chargingmode|invertermode|\.status$/i;

export function normalizeStatusValue(value) {
  if (value === null || value === undefined) return '';
  let s = String(value).trim();
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }
  return s.toLowerCase();
}

export function isStatusDataPath(path) {
  return Boolean(path && STATUS_DATA_PATH_RE.test(String(path)));
}

export function isNumericDisplayValue(value) {
  const s = String(value ?? '').trim();
  if (!s || s === '--' || s === 'No Data' || s === 'Invalid Data') return false;
  if (/[a-zA-Z]/.test(s)) return false;
  const n = parseFloat(s.replace(/[^\d.-]/g, ''));
  return !Number.isNaN(n);
}

export function defaultBalancerStatusRanges() {
  return [
    { id: 1, match: 'balanced', color: '#10b981', label: 'Balanced', alarm: 'none' },
    { id: 2, match: 'balancing', color: '#f59e0b', label: 'Balancing', alarm: 'yellow' },
    { id: 3, match: 'imbalance', color: '#ef4444', label: 'Imbalance', alarm: 'red' },
    { id: 4, match: 'unknown', color: '#94a3b8', label: 'Unknown', alarm: 'none' },
  ];
}

export function matchStatusRange(value, statusRanges) {
  if (!Array.isArray(statusRanges) || statusRanges.length === 0) return null;
  const normalized = normalizeStatusValue(value);
  if (!normalized) return null;

  for (const range of statusRanges) {
    const match = normalizeStatusValue(range.match);
    if (match && match === normalized) return range;
  }
  return null;
}

export function resolveWidgetValueColor(widget, value) {
  const config = widget?.config ?? {};
  if (value === null || value === undefined || value === '--' || value === 'No Data' || value === 'Invalid Data') {
    return '#3b82f6';
  }

  const statusRanges = config.statusRanges || [];
  const colorRanges = config.colorRanges;

  if (statusRanges.length > 0 && !isNumericDisplayValue(value)) {
    const statusRange = matchStatusRange(value, statusRanges);
    if (statusRange) return statusRange.color || '#3b82f6';
  }

  if (!colorRanges || (Array.isArray(colorRanges) && colorRanges.length === 0)) {
    return '#3b82f6';
  }

  const numericValue = parseFloat(String(value).replace(/[^\d.-]/g, ''));
  if (Number.isNaN(numericValue)) return '#3b82f6';

  if (Array.isArray(colorRanges)) {
    for (const range of colorRanges) {
      if (numericValue >= range.min && numericValue <= range.max) {
        return range.color || '#3b82f6';
      }
    }
  } else if (typeof colorRanges === 'object') {
    if (colorRanges.alarm && numericValue >= colorRanges.alarm.min && numericValue <= colorRanges.alarm.max) {
      return colorRanges.alarm.color || '#ef4444';
    }
    if (
      colorRanges.warning &&
      colorRanges.warning.min > 0 &&
      numericValue >= colorRanges.warning.min &&
      numericValue <= colorRanges.warning.max
    ) {
      return colorRanges.warning.color || '#f59e0b';
    }
    if (colorRanges.normal && numericValue >= colorRanges.normal.min && numericValue <= colorRanges.normal.max) {
      return colorRanges.normal.color || '#10b981';
    }
  }

  return '#3b82f6';
}
